import { Trans } from '@lingui/react'
import classnames from 'clsx'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'

import Button from 'views/components/atoms/Button'
import Form from 'views/components/atoms/Form'
import AfterQuerySuggestLayer from 'views/components/organisms/SearchBox/AfterQuerySuggestLayer'
import SearchBoxListItem from 'views/components/organisms/SearchBox/SearchBoxListItem'
import useMountPoints from 'views/enhancers/useMountPoints'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'
import './SearchBoxList.scss'

const SearchBoxList = ({
  mobile,
  displayResults,
  products,
  redirectData,
  query,
  handleItemClick,
  handleShowAll,
  focusedProductIndex,
  focusedSuggestionIndex,
  formAction,
  withHighlightedProduct,
  querySuggestions,
}) => {
  const { tenant, shopURL } = usePublicConfig()
  const mountPoints = useMountPoints()
  const queryStr = query ? query.toLowerCase() : null
  const searchResultsClassname = classnames('u-padding', {
    'o-SearchBox__results': !mobile,
    'o-SearchBox__results--mobile': mobile,
    'u-hidden': !displayResults,
  })
  const handleClick = event => {
    if (handleShowAll) {
      handleShowAll(event)
    }
  }

  return (
    <div className={searchResultsClassname} data-qa-id="suggest-list">
      <AfterQuerySuggestLayer
        mountPoints={mountPoints}
        tenant={tenant}
        querySuggestions={querySuggestions}
        focusedSuggestionIndex={focusedSuggestionIndex}
        redirectData={redirectData}
        shopUrl={shopURL}
      />

      <div className="o-SearchBox__top-searches-product__title u-font-weight--bold">
        <Trans id="searchBox.label.popularProducts" />
      </div>

      <ul
        className={classnames(
          'a-list-reset o-SearchBox__list u-margin--bottom',
          {
            'o-SearchBox__list--with-highlighted-product':
              withHighlightedProduct,
            'u-margin--top': true,
          }
        )}
      >
        {products.map((product, index) => (
          <SearchBoxListItem
            mobile={mobile}
            queryStr={queryStr}
            key={product.id}
            handleItemClick={handleItemClick}
            item={product}
            focused={focusedProductIndex === index}
            last={index + 1 === products.length}
          />
        ))}
      </ul>

      <div className="o-SearchBox__controls u-text-align--center">
        <Form
          className="o-SearchBox__controls__form"
          name="search-results"
          method="get"
          action={formAction}
        >
          <Button
            name="show-all-results-button"
            onClick={handleClick}
            style={!mobile ? 'buttonGhost' : null}
            className="o-SearchBox__show-all-button"
          >
            <Trans id="general.label.showAllResults" />
          </Button>
        </Form>
      </div>
    </div>
  )
}

SearchBoxList.propTypes = {
  formAction: string.isRequired,
  mobile: bool,
  query: string,
  displayResults: bool,
  products: arrayOf(SearchBoxListItem.propTypes.item),
  redirectData: arrayOf(
    shape({
      redirectTarget: string.isRequired,
      type: string.isRequired,
      suggestText: string,
    })
  ),
  handleShowAll: func,
  handleItemClick: func,
  focusedProductIndex: number,
  focusedSuggestionIndex: number,
  hideResults: func,
  handleValueChange: func,
  searchBannerRef: shape({}),
  withHighlightedProduct: bool,
  querySuggestions: arrayOf(string),
}

SearchBoxList.defaultProps = {
  mobile: false,
  query: '',
  displayResults: false,
  products: [],
  redirectData: [],
  handleShowAll: null,
  handleItemClick: null,
  focusedProductIndex: -1,
  focusedSuggestionIndex: -1,
  hideResults: null,
  handleValueChange: null,
  searchBannerRef: null,
  withHighlightedProduct: false,
  querySuggestions: [],
}

export default SearchBoxList
